import React, { useState, useEffect } from "react";
import FlowHeader from "../../../components/FlowHeader";
import { useNavigate, useLocation } from "react-router-dom";
import { usePurchase } from "../../../context/PurchaseContext";
import { useCart } from "../../../hooks/useCart";
import { authService } from "../../../services/auth";
import { paytrService } from "../../../services/paytr";

const StepProgress = () => {
  return (
    <div className="w-[427px]">
      <div className="flex items-center">
        <div className="flex items-center relative flex-1">
          {/* İlk Nokta */}
          <div className="flex flex-col items-center">
            <div className="w-4 h-4 bg-[#4F46E5] rounded-full z-10"></div>
            <span className="text-xs mt-2 text-[#4F46E5] font-medium absolute -bottom-6 whitespace-nowrap">
              Halı Detayları
            </span>
          </div>
          {/* İlk Çizgi */}
          <div className="flex-1 h-[2px] bg-[#4F46E5] ml-0"></div>
        </div>

        <div className="flex items-center relative flex-1">
          {/* İkinci Nokta */}
          <div className="flex flex-col items-center">
            <div className="w-4 h-4 bg-[#4F46E5] rounded-full z-10"></div>
            <span className="text-xs mt-2 text-[#4F46E5] font-medium absolute -bottom-6 whitespace-nowrap">
              Adres Bilgileri
            </span>
          </div>
          {/* İkinci Çizgi */}
          <div className="flex-1 h-[2px] bg-[#4F46E5]"></div>
        </div>

        <div className="flex items-center relative">
          {/* Son Nokta */}
          <div className="flex flex-col items-center">
            <div className="w-4 h-4 bg-[#4F46E5] rounded-full z-10"></div>
            <span className="text-xs mt-2 text-[#4F46E5] font-medium absolute -bottom-6 whitespace-nowrap">
              Ödeme
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const Payment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { purchaseData, resetPurchaseData } = usePurchase();
  const { cartItems, totalAmount } = useCart();
  
  // AddressDetails'dan gelen sipariş bilgileri
  const orderId = location.state?.orderId;
  const orderResponse = location.state?.orderResponse;
  
  // Form state
  const [cardName, setCardName] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [cvv, setCvv] = useState("");
  const [use3DSecure, setUse3DSecure] = useState(false);
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [paytrIframeToken, setPaytrIframeToken] = useState(null);
  const [showPaytrIframe, setShowPaytrIframe] = useState(false);
  
  // Sepetteki ilk ürünün bayi bilgilerini al
  const firstCartItem = cartItems[0];
  const dealerName = firstCartItem?.dealer_name || "Bayi";
  
  // Tahmini teslimat tarihini formatla
  const getFormattedDeliveryDate = () => {
    if (!purchaseData.estimatedDeliveryDate) return "";
    
    const date = new Date(purchaseData.estimatedDeliveryDate);
    const days = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
    const months = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
    
    const dayName = days[date.getDay()];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    
    return `${day} ${month} ${year} - ${dayName}`;
  };
  
  const formatCardNumber = (value) => {
    // Sadece rakamları al
    const numbers = value.replace(/\D/g, '');
    
    // 16 haneden fazla girmeye izin verme
    if (numbers.length > 16) return cardNumber;
    
    // 4'lü gruplar halinde formatla
    let formatted = '';
    for (let i = 0; i < numbers.length; i++) {
      if (i > 0 && i % 4 === 0) {
        formatted += ' ';
      }
      formatted += numbers[i];
    }
    
    return formatted;
  };
  
  const formatExpiryDate = (value) => {
    // Sadece rakamları al
    const numbers = value.replace(/\D/g, '');
    
    // 4 haneden fazla girmeye izin verme (MMYY)
    if (numbers.length > 4) return expiryDate;
    
    // MM/YY formatında formatla
    let formatted = '';
    if (numbers.length >= 2) {
      formatted = numbers.substring(0, 2) + '/' + numbers.substring(2);
    } else {
      formatted = numbers;
    }
    
    return formatted;
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!cardName.trim()) {
      newErrors.cardName = "Kart sahibi adı soyadı giriniz.";
    }
    
    const cardNumberDigits = cardNumber.replace(/\D/g, '');
    if (!cardNumberDigits || cardNumberDigits.length !== 16) {
      newErrors.cardNumber = "Geçerli bir kart numarası giriniz (16 hane).";
    }
    
    const expiryDigits = expiryDate.replace(/\D/g, '');
    if (!expiryDigits || expiryDigits.length !== 4) {
      newErrors.expiryDate = "Geçerli bir son kullanma tarihi giriniz (MM/YY).";
    } else {
      const month = parseInt(expiryDigits.substring(0, 2));
      if (month < 1 || month > 12) {
        newErrors.expiryDate = "Geçersiz ay.";
      }
    }
    
    if (!cvv || cvv.length !== 3) {
      newErrors.cvv = "3 haneli güvenlik kodunu giriniz.";
    }
    
    if (!agreementAccepted) {
      newErrors.agreement = "Sözleşmeyi kabul etmelisiniz.";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  useEffect(() => {
    // Sipariş ID kontrolü
    if (!orderId) {
      console.error("Sipariş ID bulunamadı!");
      alert("Sipariş bilgisi eksik. Lütfen tekrar deneyin.");
      navigate("/sepetim");
    }
  }, [orderId, navigate]);
  
  // PayTR iframe mesajlarını dinle
  useEffect(() => {
    const handlePayTRMessage = (event) => {
      if (event.origin !== "https://www.paytr.com") return;
      
      const message = event.data;
      console.log("PayTR mesajı:", message);
      
      if (message === "payment_success") {
        // Ödeme başarılı
        console.log("Ödeme başarılı!");
        setShowPaytrIframe(false);
        resetPurchaseData();
        
        navigate("/purchase/carpet-cleaning/payment-success", {
          state: {
            orderDetails: {
              orderNo: orderId,
              orderDate: new Date(),
              paymentType: use3DSecure ? "3D Secure Kredi Kartı" : "Kredi Kartı",
              amount: totalAmount,
              dealerName: dealerName,
              estimatedDelivery: getFormattedDeliveryDate()
            },
          },
        });
      } else if (message === "payment_failed") {
        // Ödeme başarısız
        console.log("Ödeme başarısız!");
        setShowPaytrIframe(false);
        alert("Ödeme işlemi başarısız oldu. Lütfen tekrar deneyin.");
      }
    };
    
    window.addEventListener("message", handlePayTRMessage);
    
    return () => {
      window.removeEventListener("message", handlePayTRMessage);
    };
  }, [orderId, totalAmount, dealerName, use3DSecure, navigate, resetPurchaseData]);

  const handlePayment = async () => {
    if (!validateForm()) {
      return;
    }
    
    if (!orderId) {
      alert("Sipariş bilgisi eksik. Lütfen tekrar deneyin.");
      return;
    }
    
    setLoading(true);
    
    try {
      // Kullanıcı bilgilerini al
      const currentUser = authService.getCurrentUser();
      
      // Sepet ürünlerini PayTR formatına çevir
      const basket = cartItems.map(item => ([
        item.product_name,
        item.price.toString(),
        item.quantity.toString()
      ]));
      
      // Expiry date'i parçala
      const [expiryMonth, expiryYear] = expiryDate.split('/');
      
      // PayTR için ödeme verilerini hazırla
      const paymentData = {
        orderId: orderId,
        email: currentUser?.email || purchaseData.contact?.email || "misafir@yikafoni.com",
        amount: totalAmount,
        use3DSecure: use3DSecure,
        userName: purchaseData.contact?.name || currentUser?.name || "Misafir Kullanıcı",
        userAddress: purchaseData.address ? 
          `${purchaseData.address.line1}` : "Adres bilgisi yok",
        userPhone: purchaseData.contact?.phone || currentUser?.phone || "",
        basket: basket,
        cardName: cardName,
        cardNumber: cardNumber.replace(/\s/g, ''), // Boşlukları kaldır
        expiryMonth: expiryMonth,
        expiryYear: `20${expiryYear}`, // YY'yi YYYY formatına çevir
        cvv: cvv
      };
      
      console.log("PayTR ödeme işlemi başlatılıyor...");
      console.log("Sipariş ID:", orderId);
      console.log("Ödeme Verileri:", { ...paymentData, cardNumber: '****', cvv: '***' });
      
      // PayTR iframe token'ı al
      const paytrResponse = await paytrService.createIframeToken(paymentData);
      
      if (paytrResponse.success) {
        // PayTR iframe'i göster
        setPaytrIframeToken(paytrResponse.token);
        setShowPaytrIframe(true);
      } else {
        throw new Error("PayTR token oluşturulamadı.");
      }
      
    } catch (error) {
      console.error("Ödeme hatası:", error);
      alert(error.message || "Ödeme işlemi sırasında bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <FlowHeader />

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Progress Steps */}
        <div className="flex justify-center">
          <div className="w-[427px]">
            <StepProgress />

            {/* Info Boxes */}
            <div className="mt-[58px] space-y-2.5 mb-8">
              <div className="inline-flex items-center border border-black rounded-full py-2 px-4">
                <span className="text-sm">
                  <span className="font-medium">Satıcı / Sağlayıcı : </span>
                  {dealerName}
                </span>
              </div>
              <div className="block">
                <div className="inline-flex items-center border border-black rounded-full py-2 px-4">
                  <span className="text-sm">
                    <span className="font-medium">Tahmini Teslim : </span>
                    {getFormattedDeliveryDate()}
                  </span>
                </div>
              </div>
              <div className="block">
                <div className="inline-flex items-center border border-black rounded-full py-2 px-4">
                  <span className="text-sm">
                    <span className="font-medium">Toplam Tutar : </span>
                    {totalAmount} TL
                  </span>
                </div>
              </div>
            </div>

            {/* Ayırıcı Çizgi */}
            <div className="mb-8">
              <div className="w-[424px] h-[0.5px] bg-black opacity-50"></div>
            </div>

            {/* Ödeme Bilgileri Formu */}
            <div>
              <h2 className="text-xl font-semibold mb-6">Ödeme Bilgileri</h2>
              <div className="space-y-4">
                {/* Kart Sahibi Ad Soyad */}
                <div className="relative">
                  <label className="block text-sm mb-1">
                    Kart Sahibi Ad Soyad
                  </label>
                  <input
                    type="text"
                    value={cardName}
                    onChange={(e) => {
                      setCardName(e.target.value);
                      if (errors.cardName) {
                        setErrors(prev => ({ ...prev, cardName: undefined }));
                      }
                    }}
                    placeholder="Ad Soyad"
                    className={`w-full p-4 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent ${
                      errors.cardName ? 'border-red-300' : 'border-gray-200'
                    }`}
                  />
                  {errors.cardName && (
                    <p className="text-sm text-red-600 mt-1">{errors.cardName}</p>
                  )}
                </div>

                {/* Kart Numarası */}
                <div className="relative">
                  <label className="block text-sm mb-1">Kart Numarası</label>
                  <input
                    type="text"
                    value={cardNumber}
                    onChange={(e) => {
                      const formatted = formatCardNumber(e.target.value);
                      setCardNumber(formatted);
                      if (errors.cardNumber) {
                        setErrors(prev => ({ ...prev, cardNumber: undefined }));
                      }
                    }}
                    placeholder="**** **** **** ****"
                    className={`w-full p-4 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent ${
                      errors.cardNumber ? 'border-red-300' : 'border-gray-200'
                    }`}
                  />
                  {errors.cardNumber && (
                    <p className="text-sm text-red-600 mt-1">{errors.cardNumber}</p>
                  )}
                </div>

                {/* Son Kullanma Tarihi */}
                <div className="relative">
                  <label className="block text-sm mb-1">
                    Son Kullanma Tarihi
                  </label>
                  <input
                    type="text"
                    value={expiryDate}
                    onChange={(e) => {
                      const formatted = formatExpiryDate(e.target.value);
                      setExpiryDate(formatted);
                      if (errors.expiryDate) {
                        setErrors(prev => ({ ...prev, expiryDate: undefined }));
                      }
                    }}
                    placeholder="MM / YY"
                    className={`w-full p-4 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent ${
                      errors.expiryDate ? 'border-red-300' : 'border-gray-200'
                    }`}
                  />
                  {errors.expiryDate && (
                    <p className="text-sm text-red-600 mt-1">{errors.expiryDate}</p>
                  )}
                </div>

                {/* Güvenlik Kodu */}
                <div className="relative">
                  <label className="block text-sm mb-1">Güvenlik Kodu</label>
                  <input
                    type="text"
                    value={cvv}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').substring(0, 3);
                      setCvv(value);
                      if (errors.cvv) {
                        setErrors(prev => ({ ...prev, cvv: undefined }));
                      }
                    }}
                    placeholder="***"
                    maxLength="3"
                    className={`w-full p-4 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent ${
                      errors.cvv ? 'border-red-300' : 'border-gray-200'
                    }`}
                  />
                  {errors.cvv && (
                    <p className="text-sm text-red-600 mt-1">{errors.cvv}</p>
                  )}
                </div>

                {/* Checkboxlar */}
                <div className="space-y-2">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="3dSecure"
                      checked={use3DSecure}
                      onChange={(e) => setUse3DSecure(e.target.checked)}
                      className="w-4 h-4 text-[#4F46E5] border-gray-300 rounded focus:ring-[#4F46E5]"
                    />
                    <label
                      htmlFor="3dSecure"
                      className="ml-2 text-sm text-gray-600"
                    >
                      3D Güvenlik ile ödeme yap.
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="agreement"
                      checked={agreementAccepted}
                      onChange={(e) => {
                        setAgreementAccepted(e.target.checked);
                        if (errors.agreement) {
                          setErrors(prev => ({ ...prev, agreement: undefined }));
                        }
                      }}
                      className="w-4 h-4 text-[#4F46E5] border-gray-300 rounded focus:ring-[#4F46E5]"
                    />
                    <label
                      htmlFor="agreement"
                      className="ml-2 text-sm text-gray-600"
                    >
                      Gizlilik, satın alma sözleşmesini okudum, kabul ediyorum.
                    </label>
                  </div>
                  {errors.agreement && (
                    <p className="text-sm text-red-600 mt-1">{errors.agreement}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Butonlar */}
            <div className="mt-8 space-y-4">
              <button
                className="w-full bg-[#4F46E5] text-white py-4 rounded-lg hover:bg-[#4338CA] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handlePayment}
                disabled={loading}
              >
                {loading ? "İşleniyor..." : "Ödeme Yap"}
              </button>
              <button
                className="w-full bg-white text-gray-600 py-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                onClick={() => navigate(-1)}
              >
                Önceki
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* PayTR Iframe Modal */}
      {showPaytrIframe && paytrIframeToken && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">Güvenli Ödeme</h3>
              <button
                onClick={() => {
                  setShowPaytrIframe(false);
                  setPaytrIframeToken(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4">
              <iframe
                src={`https://www.paytr.com/odeme/guvenli/${paytrIframeToken}`}
                id="paytriframe"
                frameBorder="0"
                scrolling="yes"
                style={{ width: '100%', height: '600px' }}
                title="PayTR Ödeme İşlemi"
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payment;
